import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Calendar, Tag as TagIcon } from 'lucide-react';
import { Button, PasscodeModal } from '../../components';
import { Tags } from '../../components/shared';
import { projectsService } from '../../services';
import GradualBlur from '../../components/GradualBlur';
import './ProjectDetail.css';

const VideoSafeContent = ({ html }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.querySelectorAll('video').forEach(video => {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    });
  }, [html]);

  return <div ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
};

export const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasscodeModalOpen, setIsPasscodeModalOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectData = await projectsService.getProjectById(id);
        setProject(projectData);

        // Check if project is protected and show modal if needed
        if (projectData.isProtected) {
          const accessGranted = sessionStorage.getItem(`project-access-${id}`);
          if (!accessGranted) {
            setIsPasscodeModalOpen(true);
          } else {
            setHasAccess(true);
          }
        } else {
          setHasAccess(true);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
        setError('Project not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="project-detail-container">
        <div className="container">
          <div className="loading-container">
            <p data-cursor-text>Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  const handlePasscodeSuccess = (passcode) => {
    return new Promise((resolve, reject) => {
      if (passcode === project.passcode) {
        sessionStorage.setItem(`project-access-${id}`, 'granted');
        setHasAccess(true);
        setIsPasscodeModalOpen(false);
        resolve();
      } else {
        reject(new Error('Incorrect passcode'));
      }
    });
  };

  const handleModalClose = () => {
    setIsPasscodeModalOpen(false);
    // Redirect back to projects if user closes modal without entering passcode
    window.history.back();
  };

  if (error || !project) {
    return (
      <div className="project-detail-container">
        <div className="container">
          <div className="error-container">
            <h2 data-cursor-text>Project Not Found</h2>
            <p data-cursor-text>The project you're looking for doesn't exist or has been removed.</p>
            <Button as={Link} to="/projects">
              <ArrowLeft size={20} />
              <span data-cursor-text>Back to Projects</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Don't render project content if it's protected and user doesn't have access
  if (project.isProtected && !hasAccess) {
    return (
      <>
        <div className="project-detail-container">
          <div className="container">
            <div className="loading-container">
              <p data-cursor-text>Checking access...</p>
            </div>
          </div>
        </div>
        <PasscodeModal
          isOpen={isPasscodeModalOpen}
          onClose={handleModalClose}
          onSuccess={handlePasscodeSuccess}
          projectTitle={project.title}
        />
      </>
    );
  }

  return (
    <div className="project-detail-container">
      <div className="container">
        <Button as={Link} to="/projects" variant="ghost" className="back-button">
          <ArrowLeft size={20} />
          <span data-cursor-text>Back to Projects</span>
        </Button>

        {project.featuredImage && (
          <div className="project-image" style={{backgroundImage: `url(${project.featuredImage})`}} />
        )}

        <section className="project-header">
          <h1 className="project-title" data-cursor-text>{project.title}</h1>
          <p className="project-description" data-cursor-text>{project.description}</p>

          {project.myRole && (
            <div className="project-my-role">
              <span className="my-role-label">My role :</span>
              <span className="my-role-value">{project.myRole}</span>
            </div>
          )}

          <div className="project-meta">
            <Tags
              tags={project.technologies}
              variant="project"
              maxTags={10}
              showMore={false}
            />
            {project.year && (
              <div className="meta-item">
                <Calendar size={14} />
                <span data-cursor-text>{project.year}</span>
              </div>
            )}
          </div>
        </section>

        {project.content && (
          <section className="content-section">
            <VideoSafeContent html={project.content} />
          </section>
        )}

        {!project.content && project.longDescription && (
          <section className="content-section">
            <VideoSafeContent html={project.longDescription} />
          </section>
        )}
      </div>
      <PasscodeModal
        isOpen={isPasscodeModalOpen}
        onClose={handleModalClose}
        onSuccess={handlePasscodeSuccess}
        projectTitle={project?.title}
      />
      <GradualBlur
        target="page"
        position="bottom"
        height="8rem"
        strength={2}
        divCount={5}
        curve="bezier"
        exponential={true}
        opacity={1}
        className="content-page-blur"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw',
          zIndex: 999
        }}
      />
    </div>
  );
};